import React, { createContext, useState, useContext } from 'react'
import Cookies from 'universal-cookie'
import { cookiesKeyNames } from '@revbox/common-fe'
import { getPathKey as getPathKeyFromHelpers, isCookiesEnabled as isCookiesEnabledFromHelpers } from '@revbox/helpers-fe'
import { CRYPTO_KEY } from '../helpers/crypto'
import { safeParse, safeStringify } from '@helpers/json-helper'

export const CookiesContext = createContext()

const urlParams = new URLSearchParams(window.location.search)
const token = urlParams.get('token')
const redirect = urlParams.get('redirect')
let isIframe = token ? true : false
if (redirect) {
    isIframe = false
}

export const isCookiesEnabled = isCookiesEnabledFromHelpers()

const cookies = new Cookies()

export const getPathKey = () => {
    return getPathKeyFromHelpers(CRYPTO_KEY)
}

export const CookiesProvider = ({ children }) => {
    const initializeState = () => {
        const keys = Object.values(cookiesKeyNames)
        const finalData = {}
        const storage = isCookiesEnabled ? cookies : localStorage

        // get all sessions from storage (pub, advertiserId)
        const allKeys = isCookiesEnabled ? document.cookie.split('; ').map(cookie => cookie.split('=')[0]) : Object.keys(localStorage)

        // filter keys by pattern "pub" or advertiserId
        const filteredKeys = allKeys.filter(key => /^pub$|^\d+$/.test(key))

        filteredKeys.forEach(key => {
            let storedData = storage?.get ? storage.get(key) : storage.getItem(key)
            finalData[key] = isCookiesEnabled ? storedData : safeParse(storedData)
        })

        const pathKey = getPathKey()

        let storedDataForPath = storage?.get ? storage.get(pathKey) : storage.getItem(pathKey)

        if (storedDataForPath) {
            finalData[pathKey] = isCookiesEnabled ? storedDataForPath : safeParse(storedDataForPath)
        } else {
            const tabData = {}
            keys.forEach(key => {
                tabData[key] = (storage?.get ? storage.get(pathKey)?.[key] : safeParse(storage.getItem(pathKey))?.[key]) || null
            })

            finalData[pathKey] = tabData

            const dataToStore = isCookiesEnabled ? tabData : safeStringify(tabData)
            storage?.set ? storage.set(pathKey, dataToStore, { path: '/' }) : storage.setItem(pathKey, dataToStore)
        }

        return finalData
    }

    const [cookiesData, setCookiesData] = useState(initializeState)

    const updateCookiesProperty = (key, value, advId = '') => {
        const pathKey = getPathKey()
        const mainKey = advId ? advId : pathKey

        if (!key || !value) return

        setCookiesData(prevData => {
            const newData = {
                ...prevData,
                [mainKey]: {
                    ...prevData[mainKey],
                    [key]: value
                }
            }

            const dataToStore = safeStringify(newData[mainKey])

            if (isCookiesEnabled) {
                cookies.set(mainKey, dataToStore, { path: '/' })
            } else {
                localStorage.setItem(mainKey, dataToStore)
            }

            return newData
        })
    }

    const removeCookiesProperty = (key, advId = '') => {
        const pathKey = getPathKey()
        const mainKey = advId ? advId : pathKey

        setCookiesData(prevData => {
            const { [key]: _, ...rest } = prevData[mainKey]

            const newData = {
                ...prevData,
                [mainKey]: rest
            }

            const dataToStore = safeStringify(rest)
            if (isCookiesEnabled) {
                cookies.set(mainKey, dataToStore, { path: '/' })
            } else {
                localStorage.setItem(mainKey, dataToStore)
            }

            return newData
        })
    }

    const getAdvertiserId = () => {
        const pathKey = getPathKey()
        return cookiesData[pathKey]?.[cookiesKeyNames.advertiserId] || null
    }

    const getToken = () => {
        const pathKey = getPathKey()
        return cookiesData[pathKey]?.[cookiesKeyNames.token]
    }

    const getPublisherId = () => {
        const pathKey = getPathKey()
        return cookiesData[pathKey]?.[cookiesKeyNames.publisherId]
    }

    const getSkinMode = () => {
        const pathKey = getPathKey()
        return cookiesData[pathKey]?.[cookiesKeyNames.skinMode]
    }

    const isDarkMode = () => {
        const pathKey = getPathKey()
        return cookiesData[pathKey]?.[cookiesKeyNames.skinMode] === 'dark'
    }

    const isAdvertiser = () => {
        const pathKey = getPathKey()
        const sidebarSkin = cookiesData[pathKey]?.[cookiesKeyNames.sidebarSkin]
        return sidebarSkin === 'advertiser'
    }

    const SUPPORTED_LANGS_ABBR = ["FR", "EN"];
    const SUPPORTED_LANGS_LABEL = ["French", "English"];

    const getLanguageAbbr = () => {
      const pathKey = getPathKey();
      const language = cookiesData[pathKey]?.[cookiesKeyNames.language]?.value ?? "";
    
      return SUPPORTED_LANGS_ABBR.includes(language) ? language : "EN";
    };

    const getLanguage = () => {
        const pathKey = getPathKey()
        const language = cookiesData[pathKey]?.[cookiesKeyNames.language]?.label ?? ""
        
        return SUPPORTED_LANGS_LABEL.includes(language) ? language : "English"
    }

    const getShowCurrency = () => {
        const pathKey = getPathKey()
        return cookiesData[pathKey]?.[cookiesKeyNames.showCurrency] || null
    }

    const getIsTourCompleted = tourKey => {
        try {
            const pathKey = getPathKey()
            return cookiesData?.[pathKey]?.[tourKey] === 'true'
        } catch {
            return false
        }
    }

    const getEmail = () => {
        const pathKey = getPathKey();
        return cookiesData?.[pathKey]?.[cookiesKeyNames.email] || null;
    };

    const getUserId = () => {
        const pathKey = getPathKey();
        return cookiesData[pathKey]?.[cookiesKeyNames.userId] || null;
    };

    const clearCookies = () => setCookiesData({});

    return (
        <CookiesContext.Provider
            value={{
                cookiesData,
                getPathKey,
                updateCookiesProperty,
                removeCookiesProperty,
                clearCookies,
                getAdvertiserId,
                getToken,
                getPublisherId,
                getSkinMode,
                isDarkMode,
                isAdvertiser,
                getLanguageAbbr,
                getLanguage,
                getShowCurrency,
                getIsTourCompleted,
                getEmail,
                getUserId
            }}
        >
            {children}
        </CookiesContext.Provider>
    )
}

export const useCookiesContext = () => useContext(CookiesContext)
